import React, {
	useState,
	useRef,
	useEffect,
} from 'react';
import Tooltip from './tooltip';

export type TTPositionType = 'mouse' | 'above' | 'below' | 'left' | 'right';
export enum TTPosition {
	mouse = 'mouse',
	above = 'above',
	below = 'below',
	left = 'left',
	right = 'right',
}
interface Props {
	children: JSX.Element;
	message?: string;
	position?: TTPositionType
}

export default function TooltipWrapper(props: Props) {
	const {
		message = '',
		position = TTPosition.above,
	} = props;

	const [randomKey] = useState(Math.random().toString(36).substring(7));

	const [showing, setShowing] = useState(false);
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

	const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
	const dissapearTimeout = useRef<NodeJS.Timeout | null>(null);

	const ttTargetRef = useRef<Element | null>(null);

	const setHoverTimeout = (mousePos: { x: number, y: number }) => {
		if (hoverTimeout.current) {
			clearTimeout(hoverTimeout.current);
		}
		hoverTimeout.current = setTimeout(() => {
			if (dissapearTimeout.current) {
				clearTimeout(dissapearTimeout.current);
			}
			setShowing(true);
			setMousePosition(mousePos);
		}, 150);
	};

	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		// check if mouse has moved at least 8px in any direciton since setting mousePos
		// before re-showing tooltip
		if (showing) {
			const xDiff = Math.abs(mousePosition.x - e.clientX);
			const yDiff = Math.abs(mousePosition.y - e.clientY);
			if (xDiff < 8 && yDiff < 8) {
				return;
			}
		}

		if (position === TTPosition.mouse) {
			if (dissapearTimeout.current) {
				clearTimeout(dissapearTimeout.current);
			}

			dissapearTimeout.current = setTimeout(() => {
				setShowing(false);
			}, 99);
		}

		setHoverTimeout({ x: e.clientX, y: e.clientY });
	};

	const clearAll = () => {
		if (hoverTimeout.current) {
			clearTimeout(hoverTimeout.current);
		}
		setShowing(false);
	};

	const backupRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const wrap = backupRef.current;
		const child = wrap?.children[0];
		if (child) {
			ttTargetRef.current = child;
		} else {
			ttTargetRef.current = backupRef.current;
		}
	}, [props.children, showing]);

	if (!props.message) return props.children;

	return (
		<div
			onMouseMove={handleMouseMove}
			onMouseLeave={clearAll}
			data-testid="tooltip-wrapper"
		>
			<Tooltip
				message={message}
				showing={showing}
				targetRef={ttTargetRef}
				mousePos={mousePosition}
				position={props.position}
			/>

			<div id={`tt-wrapper-god-${randomKey}`} ref={backupRef}>
				{props.children}
			</div>
		</div>
	);
}
